import React, { useCallback, useEffect, useState } from 'react';
import { Table } from './pilot-ui/table';
import axiosInstance from '../utils/axiosInstance';
import config from '../utils/config';
import { formatTimestamp } from '../utils/formatTimestamp';
import { S3BundleDetailResponse, S3BundleListResponse, S3BundleSummary } from '../types';
import Button from './ui/Button';

interface S3BundleBrowserProps {
  deviceName: string;
}

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const extractErrorMessage = (err: unknown, fallback: string): string => {
  const axiosError = err as { response?: { data?: { error?: string } } };
  return axiosError?.response?.data?.error ?? fallback;
};

const normalizeBundleList = (payload: unknown): S3BundleListResponse => {
  const data = payload as Partial<S3BundleListResponse> | undefined;
  if (!data || !Array.isArray(data.bundles)) {
    throw new Error('Bundle API is not available on this backend yet.');
  }

  return {
    device_name: String(data.device_name ?? ''),
    prefix: String(data.prefix ?? ''),
    bundles: data.bundles,
  };
};

const normalizeBundleDetail = (payload: unknown): S3BundleDetailResponse => {
  const data = payload as Partial<S3BundleDetailResponse> | undefined;
  if (!data || typeof data.key !== 'string' || typeof data.content !== 'string') {
    throw new Error('Bundle detail API is not available on this backend yet.');
  }

  return {
    device_name: String(data.device_name ?? ''),
    key: data.key,
    last_modified: typeof data.last_modified === 'string' ? data.last_modified : '',
    size: typeof data.size === 'number' ? data.size : 0,
    content_kind: (data.content_kind as S3BundleDetailResponse['content_kind']) ?? 'text',
    content: data.content,
  };
};

const formatOffsetLabel = (dayOffset: number): string => {
  const day = new Date();
  day.setDate(day.getDate() - dayOffset);
  const dayString = day.toLocaleDateString();
  if (dayOffset === 0) return `${dayString} (today)`;
  if (dayOffset === 1) return `${dayString} (yesterday)`;
  return `${dayString} (${dayOffset} days ago)`;
};

const S3BundleBrowser: React.FC<S3BundleBrowserProps> = ({ deviceName }) => {
  const [dayOffset, setDayOffset] = useState(0);
  const [bundles, setBundles] = useState<S3BundleSummary[]>([]);
  const [resolvedPrefix, setResolvedPrefix] = useState('');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [bundleDetail, setBundleDetail] = useState<S3BundleDetailResponse | null>(null);
  const [listLoading, setListLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [listError, setListError] = useState('');
  const [detailError, setDetailError] = useState('');

  const fetchBundles = useCallback(async (offset: number): Promise<void> => {
    setListLoading(true);
    setListError('');
    try {
      const url = `${config.deviceDebugApiUrl}/${encodeURIComponent(deviceName)}`;
      const response = await axiosInstance.get<S3BundleListResponse>(url, {
        params: {
          bundle_action: 'list',
          limit: 100,
          day_offset: offset,
        },
      });
      const normalized = normalizeBundleList(response.data);
      setBundles(normalized.bundles);
      setResolvedPrefix(normalized.prefix);
      setSelectedKey(null);
      setBundleDetail(null);
      setDetailError('');
    } catch (err: unknown) {
      setListError(extractErrorMessage(err, 'Failed to fetch S3 bundles.'));
      setBundles([]);
      setResolvedPrefix('');
      setSelectedKey(null);
      setBundleDetail(null);
    } finally {
      setListLoading(false);
    }
  }, [deviceName]);

  const fetchBundleDetail = useCallback(async (key: string): Promise<void> => {
    setDetailLoading(true);
    setDetailError('');
    setSelectedKey(key);
    try {
      const url = `${config.deviceDebugApiUrl}/${encodeURIComponent(deviceName)}`;
      const response = await axiosInstance.get<S3BundleDetailResponse>(url, {
        params: { bundle_action: 'get', key },
      });
      setBundleDetail(normalizeBundleDetail(response.data));
    } catch (err: unknown) {
      setBundleDetail(null);
      setDetailError(extractErrorMessage(err, 'Failed to fetch bundle details.'));
    } finally {
      setDetailLoading(false);
    }
  }, [deviceName]);

  useEffect(() => {
    setDayOffset(0);
    setBundles([]);
    setResolvedPrefix('');
    setSelectedKey(null);
    setBundleDetail(null);
    setListError('');
    setDetailError('');
    void fetchBundles(0);
  }, [deviceName, fetchBundles]);

  const handleLoadNewer = (): void => {
    if (dayOffset === 0 || listLoading) return;
    const nextOffset = dayOffset - 1;
    setDayOffset(nextOffset);
    void fetchBundles(nextOffset);
  };

  const handleLoadOlder = (): void => {
    if (listLoading) return;
    const nextOffset = dayOffset + 1;
    setDayOffset(nextOffset);
    void fetchBundles(nextOffset);
  };

  return (
    <div className="max-w-7xl mx-auto p-2">
      <div className="bg-card border border-border rounded-lg shadow-md p-4 mb-4">
        <h3 className="text-lg font-semibold mb-3 border-b pb-2">S3 Bundles</h3>

        <div className="flex flex-wrap items-center gap-3 mb-3">
          <Button
            variant="secondary"
            onClick={handleLoadNewer}
            disabled={listLoading || dayOffset === 0}
            title="Show the bundles this device uploaded one day later (today is the newest)"
          >
            Newer
          </Button>
          <Button
            variant="secondary"
            onClick={handleLoadOlder}
            disabled={listLoading}
            title="Show the bundles this device uploaded one day earlier"
          >
            {listLoading ? 'Loading...' : 'Older'}
          </Button>
          <Button
            variant="primary"
            onClick={() => void fetchBundles(dayOffset)}
            disabled={listLoading}
            title="List the bundles for the day shown again, to pick up any that arrived since"
          >
            Refresh
          </Button>
          <div className="text-sm text-gray-600">
            Viewing: <strong>{formatOffsetLabel(dayOffset)}</strong>
          </div>
        </div>

        {resolvedPrefix && (
          <div className="text-xs text-muted-foreground mb-2">
            Prefix used: <code>{resolvedPrefix}</code>
          </div>
        )}

        {listError && (
          <div role="alert" className="border border-status-error-border bg-status-error text-status-error-foreground text-sm p-2 rounded-sm mb-3">{listError}</div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="border rounded-md overflow-hidden">
            <div className="bg-gray-50 px-3 py-2 text-sm font-medium border-b">
              Bundles ({bundles.length})
            </div>
            <div className="max-h-[420px] overflow-auto">
              {bundles.length === 0 && !listLoading ? (
                <div className="p-3 text-sm text-gray-600">No bundles found.</div>
              ) : (
                <Table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-muted-foreground border-b">
                      <th className="px-3 py-2">Time</th>
                      <th className="px-3 py-2">Size</th>
                      <th className="px-3 py-2">Key</th>
                      <th className="px-3 py-2">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bundles.map((bundle) => (
                      <tr
                        key={bundle.key}
                        className={`cursor-pointer border-b border-border hover:bg-secondary ${selectedKey === bundle.key ? 'bg-secondary' : ''}`}
                        onClick={() => void fetchBundleDetail(bundle.key)}
                      >
                        <td className="px-3 py-2 whitespace-nowrap">{formatTimestamp(bundle.last_modified)}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{formatBytes(bundle.size)}</td>
                        <td className="px-3 py-2 break-all">{bundle.key}</td>
                        <td className="px-3 py-2">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={(event) => {
                              event.stopPropagation();
                              void fetchBundleDetail(bundle.key);
                            }}
                            title={`Show the contents of bundle ${bundle.key}`}
                            aria-pressed={selectedKey === bundle.key}
                          >
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>
          </div>

          <div className="border rounded-md overflow-hidden">
            <div className="bg-gray-50 px-3 py-2 text-sm font-medium border-b">Bundle Detail</div>
            <div className="p-3">
              {detailLoading && <div className="text-sm text-muted-foreground">Loading bundle...</div>}
              {detailError && <div role="alert" className="border border-status-error-border bg-status-error text-status-error-foreground text-sm p-2 rounded-sm">{detailError}</div>}
              {!detailLoading && !detailError && !bundleDetail && (
                <div className="text-sm text-gray-600">Select a bundle to view its contents.</div>
              )}
              {bundleDetail && !detailLoading && (
                <div className="space-y-2">
                  <div className="text-xs text-muted-foreground">
                    <div><strong>Key:</strong> {bundleDetail.key}</div>
                    <div><strong>Last Modified:</strong> {formatTimestamp(bundleDetail.last_modified)}</div>
                    <div><strong>Size:</strong> {formatBytes(bundleDetail.size)}</div>
                    <div><strong>Content:</strong> {bundleDetail.content_kind}</div>
                  </div>
                  <pre className="bg-gray-900 text-gray-100 text-xs p-3 rounded-sm max-h-[340px] overflow-auto whitespace-pre-wrap break-all">
                    {bundleDetail.content}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default S3BundleBrowser;
